import { PopperContent } from '@radix-ui/react-popper'
import { buttonReset } from 'components/Button/Button.styles'
import { Text } from 'components/Text'
import { styled } from 'stitches.config'

export const SelectList = styled('ul', {
  maxHeight: '240px',
  overflow: 'auto',

  backgroundColor: '$accent-white',
  borderRadius: '$1',
  border: '1px solid $accent-black',

  listStyle: 'none',
  display: 'none',

  scrollbarWidth: 'none',

  '& .virtual-list': {
    scrollbarWidth: 'none',
  },

  '&::-webkit-scrollbar, & .virtual-list::-webkit-scrollbar': {
    width: 0,
  },

  '&:focus': {
    outline: '1px solid $accent-black',
  },

  variants: {
    isOpen: {
      true: {
        display: 'block',
        border: '1px solid $accent-black',
      },
      false: {},
    },
  },
})

export const Option = styled('li', {
  padding: '$2 $3',
  cursor: 'pointer',
  color: '$accent-black',
  fontSize: '$small',
  lineHeight: '$small',

  variants: {
    selected: {
      true: {
        textDecoration: 'underline',
        backgroundColor: '$tertiary-200',
      },
    },
    highlighted: {
      true: {
        '@bp2': {
          textDecoration: 'underline',
          backgroundColor: '$tertiary-200',
        },
      },
    },
  },
})

export const PopperList = styled(PopperContent, {
  [`&[data-side="top"] ${SelectList}`]: {
    borderBottomLeftRadius: '0px',
    borderBottomRightRadius: '0px',
    borderBottom: 'none',
    paddingTop: '$3',
    marginTop: '50px',
  },
  [`&[data-side="bottom"] ${SelectList}`]: {
    borderTopLeftRadius: '0px',
    borderTopRightRadius: '0px',
    borderTop: 'none',
    paddingBottom: '$3',
  },
})

export const Content = styled('div', {
  fontSize: '$body',
  lineHeight: '$body',
  paddingRight: '$6',
  paddingLeft: '$2',
  alignSelf: 'center',
  variants: {
    displayLabel: {
      true: {
        paddingTop: '$1',
      },
    },
  },
})

export const SelectButton = styled('button', {
  ...buttonReset,
  alignItems: 'center',
  backgroundColor: '$accent-white',
  borderRadius: '$1',
  border: '1px solid $accent-gray',
  cursor: 'pointer',
  display: 'inline-flex',
  fontSize: '$caption',
  justifyContent: 'flex-start',
  minWidth: '200px',
  position: 'relative',
  textAlign: 'center',
  width: '100%',
  transition: 'background-color 0.1s linear',

  '&:focus, &:filled': {
    outline: '1px solid $accent-black',
  },

  '& .select-label': {
    position: 'absolute',
    left: '$2',
    transition: 'all 150ms ease-out',
    fontSize: '$small',
    lineHeight: '$small',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    color: '$accent-gray',
    width: 'initial',
  },

  '&:disabled': {
    cursor: 'not-allowed',
  },
  '&:focus': {
    outline: '1px solid $accent-black',
  },

  variants: {
    isOpen: {
      true: {
        '& .select-label': {
          fontSize: '$caption',
          lineHeight: '$caption',
          top: '0',
          transform: 'translateY(-.65rem)',
          backgroundColor: '$accent-white',
          padding: '0px 4px',
          color: '$accent-black',
        },
      },
    },
    isFilled: {
      true: {
        '& .select-label': {
          fontSize: '$caption',
          lineHeight: '$caption',
          top: '0',
          transform: 'translateY(-.65rem)',
          backgroundColor: '$accent-white',
          padding: '0px 4px',
          color: '$accent-black',
          borderColor: '$accent-black',
        },
      },
    },
    size: {
      small: {
        height: '$4',
      },
      regular: {
        height: '$6',
      },
    },
    side: {
      bottom: {},
      top: {},
    },
  },
  compoundVariants: [
    {
      side: 'bottom',
      isOpen: true,
      css: {
        borderBottomColor: '$accent-white',
        borderBottomLeftRadius: '0px',
        borderBottomRightRadius: '0px',
      },
    },
    {
      side: 'top',
      isOpen: true,
      css: {
        borderTopColor: '$accent-white',
        borderTopLeftRadius: '0px',
        borderTopRightRadius: '0px',
      },
    },
  ],
  defaultVariants: {
    size: 'regular',
  },
})

export const IconContainer = styled('div', {
  cursor: 'pointer',
  position: 'absolute',
  right: '$2',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: '$accent-black',

  '& svg': {
    transition: 'all 0.1s linear',
  },
})

export const HelperText = styled(Text, {
  color: '$surface-300',
  paddingLeft: '$1',

  variants: {
    error: {
      true: {
        color: 'rgb($tag-red)',
      },
    },
    success: {
      true: {
        color: 'rgb($tag-green)',
      },
    },
    disabled: {
      true: {
        color: '$accent-gray',
      },
    },
  },
})

export const SelectRoot = styled('div', {
  position: 'relative',

  variants: {
    widthStyle: {
      half: {
        width: '100%',
        '@bp2': {
          width: '50%',
        },
      },
      full: {
        width: '100%',
      },
    },
    error: {
      true: {
        [`& ${Content}, & .select-label`]: {
          color: 'rgb($tag-red)',
        },
        [`& ${SelectButton}`]: {
          borderColor: 'rgb($tag-red)',
          cursor: 'not-allowed',
        },
        [`& ${IconContainer}`]: {
          color: '$primary-600',
        },
        [`& ${HelperText}`]: {
          color: 'rgb($tag-red)',
        },
      },
    },
    success: {
      true: {},
    },
    disabled: {
      true: {
        [`& ${Content}, & .select-label`]: {
          color: '$accent-gray',
        },
        [`& ${SelectButton}`]: {
          borderColor: '$accent-gray',
          cursor: 'not-allowed',
        },
        [`& ${IconContainer}`]: {
          color: '$accent-gray',
        },
        [`& ${HelperText}`]: {
          color: '$accent-gray',
        },
      },
    },
    isOpen: {
      true: {
        [`& ${SelectButton}`]: {
          borderColor: '$accent-black',
        },

        [`& ${IconContainer} svg`]: {
          transform: 'rotate(-180deg)',
        },
      },
      false: {},
    },
    isShort: {
      true: {
        '& button': {
          minWidth: '100px',
        },
        '@bp2': {
          '& button': {
            minWidth: '200px',
          },
        },
      },
    },
  },

  compoundVariants: [
    {
      error: true,
      isOpen: true,
      css: {
        [`& ${SelectButton}`]: {
          borderColor: 'rgb($tag-red)',
          borderBottomColor: '$accent-white',
        },
      },
    },
  ],
})
